import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  isApiUserEmail,
  serializeRegularMemberName,
  splitRegularMemberName,
} from '@mls-wallet/shared-types';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useUpdateMemberName } from '@/hooks/admin/member/use-update-member-name';
import { getRequestErrorMessage } from '@/lib/api/get-request-error-message';
import type { OrganizationMember } from '@/types/user';

interface EditMemberNameDialogProps {
  open: boolean;
  member: OrganizationMember | null;
  onOpenChange: (open: boolean) => void;
  onUpdated: () => void;
}

export function EditMemberNameDialog({
  open,
  member,
  onOpenChange,
  onUpdated,
}: EditMemberNameDialogProps) {
  const [name, setName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { updateMemberName, isLoading } = useUpdateMemberName();

  const isApiMember = member ? isApiUserEmail(member.email) : false;
  const currentRegularName = splitRegularMemberName(member?.name ?? '');

  useEffect(() => {
    if (!open || !member) {
      setErrorMessage(null);
      return;
    }

    if (isApiUserEmail(member.email)) {
      setName(member.name);
      setFirstName('');
      setLastName('');
    } else {
      const splitName = splitRegularMemberName(member.name);
      setFirstName(splitName.firstName);
      setLastName(splitName.lastName);
      setName('');
    }

    setErrorMessage(null);
  }, [open, member]);

  const normalizedName = isApiMember
    ? name.trim()
    : serializeRegularMemberName(firstName, lastName);
  const isInvalid = isApiMember
    ? name.trim().length === 0
    : firstName.trim().length === 0 || lastName.trim().length === 0;
  const isUnchanged = isApiMember
    ? normalizedName === (member?.name.trim() ?? '')
    : firstName.trim() === currentRegularName.firstName &&
      lastName.trim() === currentRegularName.lastName;
  const isSubmitDisabled = !member || isInvalid || isUnchanged || isLoading;

  const handleSubmit = async () => {
    if (!member || isSubmitDisabled) {
      return;
    }

    setErrorMessage(null);

    try {
      if (isApiMember) {
        await updateMemberName({
          memberId: member.id,
          memberType: 'api',
          name: name.trim(),
        });
      } else {
        await updateMemberName({
          memberId: member.id,
          memberType: 'regular',
          firstName: firstName.trim(),
          lastName: lastName.trim(),
        });
      }
      onUpdated();
      onOpenChange(false);
    } catch (error) {
      setErrorMessage(
        getRequestErrorMessage(error, 'Failed to update member name.')
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit name</DialogTitle>
          <DialogDescription>
            {isApiMember
              ? "Update this API member's name."
              : "Update this member's first and last name."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {isApiMember ? (
            <div className="space-y-2">
              <Label htmlFor="member-name">Name</Label>
              <Input
                id="member-name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setErrorMessage(null);
                }}
                placeholder="Member name"
              />
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <Label htmlFor="member-first-name">First name</Label>
                <Input
                  id="member-first-name"
                  value={firstName}
                  onChange={(e) => {
                    setFirstName(e.target.value);
                    setErrorMessage(null);
                  }}
                  placeholder="First name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="member-last-name">Last name</Label>
                <Input
                  id="member-last-name"
                  value={lastName}
                  onChange={(e) => {
                    setLastName(e.target.value);
                    setErrorMessage(null);
                  }}
                  placeholder="Last name"
                />
              </div>
            </>
          )}
        </div>

        {errorMessage ? (
          <div className="text-sm text-rose-600">{errorMessage}</div>
        ) : null}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            className="relative"
          >
            <span className={isLoading ? 'invisible' : ''}>Confirm</span>
            {isLoading ? (
              <span className="absolute inset-0 flex items-center justify-center">
                <Loader2 className="h-4 w-4 animate-spin" />
              </span>
            ) : null}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
